import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../api';
import {
  BookOpen, Plus, Search, Eye, Trash2, Users, CheckCircle, Clock,
  FileText, Link2, Video, Settings, Edit2, GraduationCap, BarChart3,
  Loader2, ChevronDown, ChevronUp, AlertTriangle, Target, Award, Download
} from 'lucide-react';

const TrainingManagement = ({ user, setUser }) => {
  const [tab, setTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [autoRules, setAutoRules] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');

  // Course dialog
  const [showCourse, setShowCourse] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [courseForm, setCourseForm] = useState({ name: '', description: '', category: '', duration_hours: '', mandatory: true, modules: [] });
  const [saving, setSaving] = useState(false);

  // Assign dialog
  const [showAssign, setShowAssign] = useState(false);
  const [assignCourseId, setAssignCourseId] = useState('');
  const [selectedEmpIds, setSelectedEmpIds] = useState([]);
  const [assignDueDate, setAssignDueDate] = useState('');
  const [empSearch, setEmpSearch] = useState('');

  // Auto-rule dialog
  const [showRule, setShowRule] = useState(false);
  const [ruleCourseId, setRuleCourseId] = useState('');
  const [ruleDeptId, setRuleDeptId] = useState('');
  const [ruleDesigId, setRuleDesigId] = useState('');

  // Course detail dialog
  const [showDetail, setShowDetail] = useState(null);

  // Certificates
  const [certificates, setCertificates] = useState([]);
  const [certDownloading, setCertDownloading] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [cRes, aRes, sRes, dRes, dgRes, eRes] = await Promise.all([
        api.get('/training/courses'),
        api.get('/training/assignments'),
        api.get('/training/stats'),
        api.get('/departments'),
        api.get('/designations'),
        api.get('/employees'),
      ]);
      setCourses(cRes.data);
      setAssignments(aRes.data);
      setStats(sRes.data);
      setDepartments(Array.isArray(dRes.data) ? dRes.data : []);
      setDesignations(Array.isArray(dgRes.data) ? dgRes.data : []);
      setEmployees(Array.isArray(eRes.data) ? eRes.data : eRes.data?.employees || []);
    } catch (err) { console.error(err); }
    try {
      const rRes = await api.get('/training/auto-rules');
      setAutoRules(rRes.data);
    } catch { setAutoRules([]); }
    try {
      const certRes = await api.get('/training/certificates/all');
      setCertificates(certRes.data);
    } catch { setCertificates([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ==================== Course CRUD ====================
  const openCourseDialog = (course = null) => {
    if (course) {
      setEditCourse(course);
      setCourseForm({
        name: course.name, description: course.description || '', category: course.category || '',
        duration_hours: course.duration_hours || '', mandatory: course.mandatory,
        modules: course.modules || [],
      });
    } else {
      setEditCourse(null);
      setCourseForm({ name: '', description: '', category: '', duration_hours: '', mandatory: true, modules: [] });
    }
    setShowCourse(true);
  };

  const addModule = () => {
    setCourseForm(prev => ({
      ...prev,
      modules: [...prev.modules, {
        id: `temp-${Date.now()}`, title: '', description: '', order: prev.modules.length,
        materials: [], quiz: { questions: [], passing_score: 70 }
      }]
    }));
  };

  const updateModule = (idx, field, value) => {
    setCourseForm(prev => {
      const modules = [...prev.modules];
      modules[idx] = { ...modules[idx], [field]: value };
      return { ...prev, modules };
    });
  };

  const removeModule = (idx) => {
    setCourseForm(prev => ({ ...prev, modules: prev.modules.filter((_, i) => i !== idx) }));
  };

  const addQuestion = (moduleIdx) => {
    setCourseForm(prev => {
      const modules = [...prev.modules];
      const quiz = { ...(modules[moduleIdx].quiz || { questions: [], passing_score: 70 }) };
      quiz.questions = [...quiz.questions, { text: '', options: ['', '', '', ''], correct_answer: 0 }];
      modules[moduleIdx] = { ...modules[moduleIdx], quiz };
      return { ...prev, modules };
    });
  };

  const updateQuestion = (mIdx, qIdx, field, value) => {
    setCourseForm(prev => {
      const modules = [...prev.modules];
      const quiz = { ...modules[mIdx].quiz };
      const questions = [...quiz.questions];
      questions[qIdx] = { ...questions[qIdx], [field]: value };
      quiz.questions = questions;
      modules[mIdx] = { ...modules[mIdx], quiz };
      return { ...prev, modules };
    });
  };

  const updateOption = (mIdx, qIdx, oIdx, value) => {
    setCourseForm(prev => {
      const modules = [...prev.modules];
      const quiz = { ...modules[mIdx].quiz };
      const questions = [...quiz.questions];
      const options = [...questions[qIdx].options];
      options[oIdx] = value;
      questions[qIdx] = { ...questions[qIdx], options };
      quiz.questions = questions;
      modules[mIdx] = { ...modules[mIdx], quiz };
      return { ...prev, modules };
    });
  };

  const removeQuestion = (mIdx, qIdx) => {
    setCourseForm(prev => {
      const modules = [...prev.modules];
      const quiz = { ...modules[mIdx].quiz };
      quiz.questions = quiz.questions.filter((_, i) => i !== qIdx);
      modules[mIdx] = { ...modules[mIdx], quiz };
      return { ...prev, modules };
    });
  };

  const addMaterial = (moduleIdx, type) => {
    setCourseForm(prev => {
      const modules = [...prev.modules];
      modules[moduleIdx] = {
        ...modules[moduleIdx],
        materials: [...(modules[moduleIdx].materials || []), { type, title: '', url: '' }]
      };
      return { ...prev, modules };
    });
  };

  const updateMaterial = (mIdx, matIdx, field, value) => {
    setCourseForm(prev => {
      const modules = [...prev.modules];
      const materials = [...modules[mIdx].materials];
      materials[matIdx] = { ...materials[matIdx], [field]: value };
      modules[mIdx] = { ...modules[mIdx], materials };
      return { ...prev, modules };
    });
  };

  const removeMaterial = (mIdx, matIdx) => {
    setCourseForm(prev => {
      const modules = [...prev.modules];
      modules[mIdx] = { ...modules[mIdx], materials: modules[mIdx].materials.filter((_, i) => i !== matIdx) };
      return { ...prev, modules };
    });
  };

  const saveCourse = async () => {
    if (!courseForm.name) { toast.error('Course name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...courseForm,
        duration_hours: parseFloat(courseForm.duration_hours) || 0,
        modules: courseForm.modules.map((m, i) => ({
          ...m, order: i,
          quiz: m.quiz && m.quiz.questions?.length > 0 ? m.quiz : null,
        })),
      };
      if (editCourse) {
        await api.put(`/training/courses/${editCourse.id}`, payload);
        toast.success('Course updated');
      } else {
        await api.post('/training/courses', payload);
        toast.success('Course created');
      }
      setShowCourse(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const deleteCourse = async (id) => {
    if (!window.confirm('Archive this course?')) return;
    try { await api.delete(`/training/courses/${id}`); toast.success('Archived'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  // ==================== Assignment ====================
  const toggleEmp = (id) => {
    setSelectedEmpIds(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);
  };

  const handleAssign = async () => {
    if (!assignCourseId || selectedEmpIds.length === 0) { toast.error('Select course and employees'); return; }
    try {
      const res = await api.post('/training/assignments', {
        course_id: assignCourseId, employee_ids: selectedEmpIds, due_date: assignDueDate || undefined
      });
      toast.success(res.data.message);
      setShowAssign(false);
      setSelectedEmpIds([]);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // ==================== Auto-rules ====================
  const handleCreateRule = async () => {
    if (!ruleCourseId) { toast.error('Select a course'); return; }
    try {
      await api.post('/training/auto-rules', {
        course_id: ruleCourseId,
        department_id: ruleDeptId || undefined,
        designation_id: ruleDesigId || undefined,
      });
      toast.success('Auto-rule created');
      setShowRule(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteRule = async (id) => {
    try { await api.delete(`/training/auto-rules/${id}`); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const downloadCert = async (assignmentId, cert) => {
    setCertDownloading(assignmentId);
    try {
      const res = await api.get(`/training/certificates/${assignmentId}/download`, { responseType: 'blob' });
      const contentDisp = res.headers?.['content-disposition'] || '';
      const serverFilename = contentDisp.match(/filename="?([^";\n]+)"?/)?.[1];
      const fallbackName = `Certificate_${(cert?.employee_code || assignmentId).replace(/\s/g, '_')}_${(cert?.course_name || 'Training').replace(/\s/g, '_')}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = serverFilename || fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
    finally { setCertDownloading(null); }
  };

  const filteredCourses = courses.filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()));
  const filteredAssignments = assignments.filter(a => !search || a.employee_name?.toLowerCase().includes(search.toLowerCase()) || a.course_name?.toLowerCase().includes(search.toLowerCase()));

  const getStatusBadge = (status) => ({
    assigned: <Badge className="bg-slate-100 text-slate-700">Assigned</Badge>,
    in_progress: <Badge className="bg-blue-100 text-blue-700">In Progress</Badge>,
    completed: <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>,
    failed: <Badge className="bg-red-100 text-red-700">Failed</Badge>,
  }[status] || <Badge variant="outline">{status}</Badge>);

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="training-management-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">Training Management</h1>
            <p className="text-slate-500 text-sm">Create courses, assign training, and track completion</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { setShowAssign(true); setAssignCourseId(''); setSelectedEmpIds([]); setEmpSearch(''); }} data-testid="assign-btn">
              <Users size={16} className="mr-1" /> Assign
            </Button>
            <Button onClick={() => openCourseDialog()} data-testid="create-course-btn">
              <Plus size={16} className="mr-1" /> New Course
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: 'Courses', value: stats.total_courses || 0, color: 'indigo', icon: BookOpen },
            { label: 'Assigned', value: stats.assigned || 0, color: 'slate', icon: Clock },
            { label: 'In Progress', value: stats.in_progress || 0, color: 'blue', icon: Target },
            { label: 'Completed', value: stats.completed || 0, color: 'emerald', icon: CheckCircle },
            { label: 'Total', value: stats.total_assignments || 0, color: 'purple', icon: Users },
            { label: 'Overdue', value: stats.overdue || 0, color: 'red', icon: AlertTriangle },
          ].map(s => (
            <Card key={s.label} className={`border-l-4 border-l-${s.color}-400`}>
              <CardContent className="p-3">
                <p className={`text-xs text-${s.color}-600`}>{s.label}</p>
                <p className={`text-xl font-bold text-${s.color}-700`}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="courses" data-testid="tab-courses">Courses</TabsTrigger>
              <TabsTrigger value="assignments" data-testid="tab-assignments">Assignments</TabsTrigger>
              <TabsTrigger value="certificates" data-testid="tab-certificates">Certificates</TabsTrigger>
              <TabsTrigger value="auto-rules" data-testid="tab-auto-rules">Auto-Rules</TabsTrigger>
            </TabsList>
            <div className="relative max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-64" data-testid="search-input" />
            </div>
          </div>

          {/* COURSES TAB */}
          <TabsContent value="courses">
            {loading ? <div className="py-16 text-center text-slate-400">Loading...</div> : filteredCourses.length === 0 ? (
              <div className="py-16 text-center text-slate-400">
                <BookOpen size={40} className="mx-auto mb-3 text-slate-300" />
                <p>No courses yet. Create your first training course.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredCourses.map(c => (
                  <Card key={c.id} className="hover:shadow-md transition-shadow" data-testid={`course-card-${c.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base">{c.name}</CardTitle>
                          <p className="text-xs text-slate-400 mt-1">{c.category || 'General'} | {c.duration_hours || 0}h | {(c.modules || []).length} modules</p>
                        </div>
                        <div className="flex gap-1">
                          {c.mandatory && <Badge className="bg-red-100 text-red-700 text-xs">Mandatory</Badge>}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-500 line-clamp-2 mb-3">{c.description || 'No description'}</p>
                      <div className="flex items-center justify-between text-xs text-slate-400">
                        <span>{c.total_assigned || 0} assigned | {c.total_completed || 0} completed</span>
                      </div>
                      <div className="flex items-center gap-1 mt-3 pt-3 border-t">
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setShowDetail(c)} data-testid={`view-course-${c.id}`}><Eye size={12} className="mr-1" /> View</Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openCourseDialog(c)}><Edit2 size={12} className="mr-1" /> Edit</Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500" onClick={() => deleteCourse(c.id)}><Trash2 size={12} className="mr-1" /> Archive</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ASSIGNMENTS TAB */}
          <TabsContent value="assignments">
            <Card>
              <CardContent className="p-0">
                {filteredAssignments.length === 0 ? (
                  <div className="py-16 text-center text-slate-400">No assignments</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50 text-left text-slate-500">
                          <th className="p-3 font-medium">Employee</th>
                          <th className="p-3 font-medium">Course</th>
                          <th className="p-3 font-medium text-center">Progress</th>
                          <th className="p-3 font-medium text-center">Status</th>
                          <th className="p-3 font-medium">Due Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAssignments.map(a => (
                          <tr key={a.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`assignment-row-${a.id}`}>
                            <td className="p-3">
                              <p className="font-medium text-slate-800">{a.employee_name}</p>
                              <p className="text-xs text-slate-400">{a.employee_code} | {a.department_name}</p>
                            </td>
                            <td className="p-3 text-slate-700">{a.course_name}</td>
                            <td className="p-3">
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${a.progress_percentage >= 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${a.progress_percentage}%` }} />
                                </div>
                                <span className="text-xs">{a.progress_percentage}%</span>
                              </div>
                            </td>
                            <td className="p-3 text-center">{getStatusBadge(a.status)}</td>
                            <td className="p-3 text-slate-500 text-xs">{a.due_date ? new Date(a.due_date).toLocaleDateString('en-IN') : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* CERTIFICATES TAB */}
          <TabsContent value="certificates">
            <Card>
              <CardContent className="p-0">
                {certificates.length === 0 ? (
                  <div className="py-16 text-center text-slate-400">
                    <Award size={40} className="mx-auto mb-3 text-slate-300" />
                    <p>No certificates issued yet. Certificates are auto-generated when employees complete training.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="certificates-table">
                      <thead className="bg-slate-50 border-b text-left">
                        <tr>
                          <th className="p-3 font-medium text-slate-600">Certificate ID</th>
                          <th className="p-3 font-medium text-slate-600">Employee</th>
                          <th className="p-3 font-medium text-slate-600">Course</th>
                          <th className="p-3 font-medium text-slate-600">Score</th>
                          <th className="p-3 font-medium text-slate-600">Completed</th>
                          <th className="p-3 font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {certificates.map(cert => (
                          <tr key={cert.id} className="hover:bg-slate-50" data-testid={`cert-row-${cert.certificate_id}`}>
                            <td className="p-3 font-mono text-xs text-indigo-600">{cert.certificate_id}</td>
                            <td className="p-3">
                              <p className="font-medium text-slate-800">{cert.employee_name}</p>
                              <p className="text-xs text-slate-400">{cert.employee_code}</p>
                            </td>
                            <td className="p-3 text-slate-700">{cert.course_name}</td>
                            <td className="p-3">
                              {cert.score != null ? (
                                <Badge className="bg-indigo-100 text-indigo-700">{cert.score}%</Badge>
                              ) : <span className="text-slate-400">-</span>}
                            </td>
                            <td className="p-3 text-slate-600">{cert.completed_date}</td>
                            <td className="p-3">
                              <Button size="sm" variant="ghost" className="h-7 text-indigo-600"
                                onClick={() => downloadCert(cert.assignment_id, cert)}
                                disabled={certDownloading === cert.assignment_id}
                                data-testid={`download-cert-admin-${cert.certificate_id}`}>
                                <Download size={14} className="mr-1" />
                                {certDownloading === cert.assignment_id ? 'Generating...' : 'PDF'}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AUTO-RULES TAB */}
          <TabsContent value="auto-rules">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-slate-500">Auto-assign courses when new employees are created based on their department or designation.</p>
                <Button size="sm" onClick={() => { setShowRule(true); setRuleCourseId(''); setRuleDeptId(''); setRuleDesigId(''); }} data-testid="add-rule-btn">
                  <Plus size={14} className="mr-1" /> Add Rule
                </Button>
              </div>
              {autoRules.length === 0 ? (
                <div className="py-12 text-center text-slate-400">No auto-assign rules configured.</div>
              ) : (
                <div className="space-y-2">
                  {autoRules.map(r => (
                    <div key={r.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border" data-testid={`rule-${r.id}`}>
                      <div>
                        <p className="text-sm font-medium">{r.course_name}</p>
                        <p className="text-xs text-slate-500">Dept: {r.department_name || 'All'} | Designation: {r.designation_name || 'All'}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteRule(r.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ==================== COURSE DIALOG ==================== */}
      <Dialog open={showCourse} onOpenChange={setShowCourse}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editCourse ? 'Edit Course' : 'Create Course'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Course Name <span className="text-red-400">*</span></Label>
                <Input value={courseForm.name} onChange={e => setCourseForm({ ...courseForm, name: e.target.value })} placeholder="e.g. Sales Onboarding" data-testid="course-name" />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea value={courseForm.description} onChange={e => setCourseForm({ ...courseForm, description: e.target.value })} placeholder="Course description..." rows={2} />
              </div>
              <div>
                <Label>Category</Label>
                <Input value={courseForm.category} onChange={e => setCourseForm({ ...courseForm, category: e.target.value })} placeholder="e.g. Onboarding, Compliance" />
              </div>
              <div>
                <Label>Duration (hours)</Label>
                <Input type="number" value={courseForm.duration_hours} onChange={e => setCourseForm({ ...courseForm, duration_hours: e.target.value })} />
              </div>
              <div className="flex items-center gap-2 col-span-2">
                <input type="checkbox" checked={courseForm.mandatory} onChange={e => setCourseForm({ ...courseForm, mandatory: e.target.checked })} className="rounded" id="mandatory" />
                <label htmlFor="mandatory" className="text-sm text-slate-700">Mandatory (blocks probation confirmation if incomplete)</label>
              </div>
            </div>

            {/* Modules */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Modules</Label>
                <Button size="sm" variant="outline" onClick={addModule} data-testid="add-module-btn"><Plus size={14} className="mr-1" /> Add Module</Button>
              </div>
              {courseForm.modules.map((mod, mIdx) => (
                <Card key={mod.id || mIdx} className="mb-3 border-indigo-100" data-testid={`module-${mIdx}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold text-indigo-700">Module {mIdx + 1}</Label>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400" onClick={() => removeModule(mIdx)}><Trash2 size={12} /></Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Module title" value={mod.title} onChange={e => updateModule(mIdx, 'title', e.target.value)} data-testid={`module-title-${mIdx}`} />
                      <Input placeholder="Description" value={mod.description || ''} onChange={e => updateModule(mIdx, 'description', e.target.value)} />
                    </div>

                    {/* Materials */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Label className="text-xs text-slate-500">Materials</Label>
                        <Button size="sm" variant="ghost" className="h-5 text-xs px-1" onClick={() => addMaterial(mIdx, 'link')}><Link2 size={10} className="mr-0.5" /> Link</Button>
                        <Button size="sm" variant="ghost" className="h-5 text-xs px-1" onClick={() => addMaterial(mIdx, 'video')}><Video size={10} className="mr-0.5" /> Video</Button>
                      </div>
                      {(mod.materials || []).map((mat, matIdx) => (
                        <div key={matIdx} className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs w-14 justify-center">{mat.type}</Badge>
                          <Input placeholder="Title" value={mat.title} onChange={e => updateMaterial(mIdx, matIdx, 'title', e.target.value)} className="h-7 text-xs" />
                          <Input placeholder="URL" value={mat.url} onChange={e => updateMaterial(mIdx, matIdx, 'url', e.target.value)} className="h-7 text-xs" />
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400" onClick={() => removeMaterial(mIdx, matIdx)}><Trash2 size={10} /></Button>
                        </div>
                      ))}
                    </div>

                    {/* Quiz */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs text-slate-500">Quiz / Assessment</Label>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs">Pass: </Label>
                          <Input type="number" className="h-6 w-16 text-xs" value={mod.quiz?.passing_score || 70} onChange={e => {
                            const quiz = { ...(mod.quiz || { questions: [], passing_score: 70 }), passing_score: parseInt(e.target.value) || 70 };
                            updateModule(mIdx, 'quiz', quiz);
                          }} />
                          <span className="text-xs text-slate-400">%</span>
                          <Button size="sm" variant="ghost" className="h-5 text-xs px-1" onClick={() => addQuestion(mIdx)} data-testid={`add-question-${mIdx}`}><Plus size={10} /> Q</Button>
                        </div>
                      </div>
                      {(mod.quiz?.questions || []).map((q, qIdx) => (
                        <div key={qIdx} className="bg-slate-50 p-2 rounded mb-1.5 text-xs" data-testid={`question-${mIdx}-${qIdx}`}>
                          <div className="flex items-center gap-1 mb-1">
                            <span className="text-slate-500 font-semibold">Q{qIdx + 1}.</span>
                            <Input placeholder="Question text" value={q.text} onChange={e => updateQuestion(mIdx, qIdx, 'text', e.target.value)} className="h-6 text-xs flex-1" data-testid={`q-text-${mIdx}-${qIdx}`} />
                            <Button variant="ghost" size="icon" className="h-5 w-5 text-red-400" onClick={() => removeQuestion(mIdx, qIdx)}><Trash2 size={10} /></Button>
                          </div>
                          <div className="grid grid-cols-2 gap-1 ml-6">
                            {q.options.map((opt, oIdx) => (
                              <div key={oIdx} className="flex items-center gap-1">
                                <input type="radio" name={`correct-${mIdx}-${qIdx}`} checked={q.correct_answer === oIdx} onChange={() => updateQuestion(mIdx, qIdx, 'correct_answer', oIdx)} className="w-3 h-3" />
                                <Input placeholder={`Option ${oIdx + 1}`} value={opt} onChange={e => updateOption(mIdx, qIdx, oIdx, e.target.value)} className="h-5 text-xs" />
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCourse(false)}>Cancel</Button>
            <Button onClick={saveCourse} disabled={saving} data-testid="save-course-btn">
              {saving && <Loader2 size={14} className="animate-spin mr-1" />} {editCourse ? 'Update' : 'Create'} Course
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== ASSIGN DIALOG ==================== */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Assign Course to Employees</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Course</Label>
              <Select value={assignCourseId} onValueChange={setAssignCourseId}>
                <SelectTrigger data-testid="assign-course-select"><SelectValue placeholder="Select course" /></SelectTrigger>
                <SelectContent>
                  {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input type="date" value={assignDueDate} onChange={e => setAssignDueDate(e.target.value)} />
            </div>
            <div>
              <Label>Employees ({selectedEmpIds.length} selected)</Label>
              <Input placeholder="Search employees..." value={empSearch} onChange={e => setEmpSearch(e.target.value)} className="mb-2" />
              <div className="max-h-48 overflow-y-auto border rounded divide-y">
                {employees.filter(e => !empSearch || (e.name || '').toLowerCase().includes(empSearch.toLowerCase()) || (e.employee_id || '').toLowerCase().includes(empSearch.toLowerCase())).slice(0, 20).map(emp => {
                  const name = emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
                  return (
                    <label key={emp.id} className="flex items-center gap-2 px-3 py-2 hover:bg-indigo-50 cursor-pointer">
                      <input type="checkbox" checked={selectedEmpIds.includes(emp.id)} onChange={() => toggleEmp(emp.id)} className="rounded" />
                      <span className="text-sm">{name}</span>
                      <span className="text-xs text-slate-400 ml-auto">{emp.employee_id}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={!assignCourseId || selectedEmpIds.length === 0} data-testid="confirm-assign-btn">Assign to {selectedEmpIds.length} Employees</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== AUTO-RULE DIALOG ==================== */}
      <Dialog open={showRule} onOpenChange={setShowRule}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Auto-Assign Rule</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Course</Label>
              <Select value={ruleCourseId} onValueChange={setRuleCourseId}>
                <SelectTrigger data-testid="rule-course-select"><SelectValue placeholder="Select course" /></SelectTrigger>
                <SelectContent>
                  {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Department (optional)</Label>
              <Select value={ruleDeptId || 'all'} onValueChange={v => setRuleDeptId(v === 'all' ? '' : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Designation (optional)</Label>
              <Select value={ruleDesigId || 'all'} onValueChange={v => setRuleDesigId(v === 'all' ? '' : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Designations</SelectItem>
                  {designations.map(d => <SelectItem key={d.id} value={d.id}>{d.title || d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRule(false)}>Cancel</Button>
            <Button onClick={handleCreateRule} disabled={!ruleCourseId} data-testid="save-rule-btn">Create Rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== COURSE DETAIL DIALOG ==================== */}
      {showDetail && (
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><BookOpen size={18} className="text-indigo-500" /> {showDetail.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">{showDetail.category || 'General'}</Badge>
                <Badge variant="outline">{showDetail.duration_hours || 0} hours</Badge>
                <Badge variant="outline">{(showDetail.modules || []).length} modules</Badge>
                {showDetail.mandatory && <Badge className="bg-red-100 text-red-700">Mandatory</Badge>}
              </div>
              <p className="text-sm text-slate-600">{showDetail.description}</p>

              {(showDetail.modules || []).map((m, i) => (
                <Card key={i}>
                  <CardHeader className="py-2 px-4">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <GraduationCap size={14} className="text-indigo-500" /> Module {i + 1}: {m.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-3 text-sm">
                    {m.description && <p className="text-slate-500 mb-2">{m.description}</p>}
                    {(m.materials || []).length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs font-medium text-slate-500 mb-1">Materials:</p>
                        {m.materials.map((mat, j) => (
                          <a key={j} href={mat.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-indigo-600 hover:underline text-xs mb-0.5">
                            {mat.type === 'video' ? <Video size={12} /> : mat.type === 'link' ? <Link2 size={12} /> : <FileText size={12} />}
                            {mat.title || mat.url}
                          </a>
                        ))}
                      </div>
                    )}
                    {m.quiz && m.quiz.questions?.length > 0 && (
                      <p className="text-xs text-slate-500">Quiz: {m.quiz.questions.length} questions | Pass: {m.quiz.passing_score}%</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
};

export default TrainingManagement;
